import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Navigate, useLocation } from 'react-router-dom';
import { RootState } from '../redux/store';
import { loginSuccess, initializationComplete, requireOnboarding } from '../redux/authSlice';
import { isDevAccount } from '../services/devAccountMiddleware';

interface AuthFlowProps {
  children: React.ReactNode;
}

const AuthFlow: React.FC<AuthFlowProps> = ({ children }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  
  const { 
    isAuthenticated, 
    isInitializing, 
    hasCompletedOnboarding, 
    needsOnboarding,
    token,
    email 
  } = useSelector((state: RootState) => state.auth);

  // Initialize authentication state on mount
  useEffect(() => {
    const initializeAuth = async () => {
      const storedToken = localStorage.getItem('token');
      const storedEmail = localStorage.getItem('email');
      const storedOnboardingStatus = localStorage.getItem('hasCompletedOnboarding');

      if (storedToken && storedEmail) {
        try {
          // Verify token with backend
          const response = await fetch('/api/auth/verify', {
            headers: {
              'Authorization': `Bearer ${storedToken}`,
              'X-User-Email': storedEmail
            }
          });

          if (response.ok) {
            const userData = await response.json();
            
            // Check if user has completed onboarding
            const hasOnboarded = storedOnboardingStatus === 'true' || userData.hasCompletedOnboarding;
            
            // For dev accounts like stackcurious@gmail.com, always require onboarding for testing
            const isDev = isDevAccount(storedEmail);
            const needsOnboard = isDev || !hasOnboarded;

            dispatch(loginSuccess({
              token: storedToken,
              email: storedEmail,
              hasCompletedOnboarding: !needsOnboard,
              user: userData.user
            }));

            // For dev accounts, always force onboarding
            if (isDev) {
              dispatch(requireOnboarding());
            }
          } else {
            // Token is invalid, clear auth data
            localStorage.removeItem('token');
            localStorage.removeItem('email');
            localStorage.removeItem('hasCompletedOnboarding');
            dispatch(initializationComplete());
          }
        } catch (error) {
          console.error('Auth verification failed:', error);
          // Clear auth data on error
          localStorage.removeItem('token');
          localStorage.removeItem('email');
          localStorage.removeItem('hasCompletedOnboarding');
          dispatch(initializationComplete());
        }
      } else {
        // No stored credentials
        dispatch(initializationComplete());
      }
    };

    initializeAuth();
  }, [dispatch]);

  // Show loading spinner during initialization
  if (isInitializing) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)'
      }}>
        <div style={{
          width: '50px',
          height: '50px',
          border: '5px solid rgba(76, 29, 149, 0.3)',
          borderTop: '5px solid #4338ca',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }}></div>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  // Handle authentication flow
  if (!isAuthenticated) {
    // User is not authenticated, redirect to auth page
    if (location.pathname !== '/auth' && location.pathname !== '/') {
      return <Navigate to="/auth" replace />;
    }
    return <>{children}</>;
  }

  // User is authenticated
  if (isAuthenticated && needsOnboarding && !hasCompletedOnboarding) {
    // User needs to complete onboarding
    if (location.pathname !== '/onboarding') {
      return <Navigate to="/onboarding" replace />;
    }
    return <>{children}</>;
  }

  // User is fully authenticated and onboarded
  if (isAuthenticated && hasCompletedOnboarding && !needsOnboarding) {
    // Redirect away from auth and onboarding pages
    if (location.pathname === '/auth' || location.pathname === '/onboarding') {
      return <Navigate to="/dashboard" replace />;
    }
    return <>{children}</>;
  }

  // Default fallback
  return <>{children}</>;
};

export default AuthFlow;